import {Link, useLocation, useNavigate} from "react-router-dom";

import {yupResolver} from "@hookform/resolvers/yup";
import {useForm} from "react-hook-form";
import * as yup from "yup";

import {toast} from "react-toastify";
import styles from "./ForgotPasswordSetNewPasswordPage.module.css";
import {useEffect, useMemo, useState} from "react";
import {FiEye, FiEyeOff} from "react-icons/fi";
import {confirmForgotPasswordSetNewPassword} from "../../../services/auth";
import {useTranslation} from "react-i18next";

const ForgotPasswordSetNewPasswordPage = () => {
    const {t} = useTranslation();
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showNewPasswordRepeat, setShowNewPasswordRepeat] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    // Fall back to sessionStorage (set in ForgotPasswordPage) so reloads during
    // the flow don't drop the token. Cleared once the password is successfully
    // reset, below.
    const verificationToken =
        location.state?.verificationToken ||
        sessionStorage.getItem("gredus.forgotPasswordToken");

    useEffect(() => {
        if (!verificationToken) {
            navigate("/forgot-password", {replace: true});
        }
    }, [verificationToken, navigate]);

    const schema = useMemo(
        () =>
            yup.object().shape({
                newPassword: yup
                    .string()
                    .required(t("validation.passwordRequired"))
                    .min(8, t("validation.passwordMinLength"))
                    .max(20, t("validation.passwordMaxLength"))
                    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/, t("validation.passwordComplexity")),
                newPasswordRepeat: yup
                    .string()
                    .required(t("validation.passwordRequired"))
                    .oneOf([yup.ref("newPassword"), null], t("validation.passwordsMustMatch")),
            }),
        [t]
    );

    const {
        register,
        handleSubmit,
        formState: {errors, isSubmitting},
    } = useForm({
        resolver: yupResolver(schema),
        mode: "onBlur",
    });

    const onSubmit = async (values) => {
        try {
            setError("");
            setLoading(true);
            if (values.newPassword !== values.newPasswordRepeat) {
                setError(t("auth.setNewPasswordsNotEqual"))
                setLoading(false);
                return;
            }
            const data = await confirmForgotPasswordSetNewPassword(verificationToken, values.newPassword);
            // Reset succeeded — token is now consumed server-side, clear our copy.
            sessionStorage.removeItem("gredus.forgotPasswordToken");
            toast.success(t("toasts.passwordChanged"), {
                toastId: "forgot-password-set-new-password-success",
            });
            window.location.replace(`https://${data.data.tenantSlug}.gredus.com/login`);
        } catch (err) {
            const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err.message ||
                t("auth.setNewPasswordError");
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.wrapper}>
            <Link to="/">
                <img src="/gredus-logo.svg" alt="logo" className={styles.logo}/>
            </Link>

            <div className={styles.card}>
                <h2 className={styles.title}>{t("auth.setNewPasswordTitle")}</h2>

                <form onSubmit={handleSubmit(onSubmit)} className={styles.form}>
                    {error && <p className={styles.error}>{error}</p>}

                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t("auth.newPassword")}</label>

                        <div className={styles.passwordWrap}>
                            <input
                                type={showNewPassword ? "text" : "password"}
                                {...register("newPassword")}
                                className={`${styles.input} ${styles.passwordInput}`}
                            />

                            <button
                                type="button"
                                className={styles.passwordToggle}
                                onClick={() => setShowNewPassword((v) => !v)}
                                aria-label={showNewPassword ? t("auth.hidePassword") : t("auth.showPassword")}
                            >
                                {showNewPassword ? <FiEyeOff size={18}/> : <FiEye size={18}/>}
                            </button>
                        </div>

                        {errors.newPassword && <p className={styles.error}>{errors.newPassword.message}</p>}
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.label}>{t("auth.setNewPasswordRepeatLabel")}</label>

                        <div className={styles.passwordWrap}>
                            <input
                                type={showNewPasswordRepeat ? "text" : "password"}
                                {...register("newPasswordRepeat")}
                                className={`${styles.input} ${styles.passwordInput}`}
                            />

                            <button
                                type="button"
                                className={styles.passwordToggle}
                                onClick={() => setShowNewPasswordRepeat((v) => !v)}
                                aria-label={showNewPasswordRepeat ? t("auth.hidePassword") : t("auth.showPassword")}
                            >
                                {showNewPasswordRepeat ? <FiEyeOff size={18}/> : <FiEye size={18}/>}
                            </button>
                        </div>

                        {errors.newPasswordRepeat && <p className={styles.error}>{errors.newPasswordRepeat.message}</p>}
                    </div>

                    <button type="submit" disabled={isSubmitting || loading} className={styles.submitBtn}>
                        {isSubmitting || loading ? t("auth.loginInProgress") : t("auth.loginAction")}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ForgotPasswordSetNewPasswordPage;
